import { Compass, Handshake, BadgeCheck, Flag } from "lucide-react";
import PageHeader from "../components/PageHeader";
import CTABanner from "../components/CTABanner";
import { Reveal } from "../components/motion";
import useMeta from "../lib/useMeta";
import { breadcrumb } from "../lib/seo";

const VALUES = [
  { icon: Compass, title: "Guide, not vendor", text: "Like a Saarthi, we chart the route — vendor selection, negotiation, logistics — so you can focus on the destination." },
  { icon: Handshake, title: "Trust over transactions", text: "Transparent commercial structures, honest timelines and one accountable partner for every category we handle." },
  { icon: BadgeCheck, title: "Execution obsessed", text: "Clear quotations, tracked deliveries and follow-ups without being asked. Reliability is the product." },
  { icon: Flag, title: "Indian at heart", text: "Built on India's vendor networks and business values — personal, dependable and quietly sophisticated." },
];

export default function About() {
  useMeta({
    title: "About Saarthva | Procurement & Operations Partner",
    description: "Learn how Saarthva helps businesses manage procurement, suppliers and day-to-day operations with one accountable partner across India.",
    path: "/about",
    schemas: [breadcrumb("About", "/about")],
  });
  return (
    <div data-testid="about-page">
      <PageHeader
        eyebrow="ABOUT SAARTHVA"
        title="A Saarthi for"
        accent="modern business."
        description="Saarthva is inspired by the idea of a Saarthi — the trusted guide who drives the chariot while you focus on the road ahead. We are that partner for procurement, sourcing and business operations."
      />

      <section className="bg-white" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="font-mono text-[11px] tracking-[0.3em] text-steel mb-4">OUR STORY</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-navy leading-tight">
              Businesses run on a thousand small things. We make sure none of them stop you.
            </h2>
            <div className="mt-6 space-y-4 text-slate-600 leading-relaxed">
              <p>
                Every office, hotel, clinic and warehouse depends on an invisible engine — supplies that never
                run out, vendors who answer, equipment that arrives on time. When that engine falters,
                everything slows down.
              </p>
              <p>
                Saarthva was founded to be that engine. One accountable partner who sources what you need,
                coordinates the vendors behind it and keeps the day-to-day operations running — so your team
                can do the work that actually matters.
              </p>
              <p className="font-semibold text-navy">Supply. Source. Run. That is the whole promise.</p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-3 rounded-3xl border border-saffron/30 translate-x-4 translate-y-4" />
              <img
                src="https://images.pexels.com/photos/38195854/pexels-photo-38195854.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Organised warehouse shelving with business supplies"
                data-testid="about-story-image"
                className="relative rounded-3xl object-cover w-full h-[420px] lg:h-[500px]"
                loading="lazy"
              />
              <div className="absolute bottom-5 left-5 bg-navy/90 backdrop-blur text-white rounded-2xl px-5 py-4">
                <p className="font-display text-lg font-bold text-saffron leading-tight">Built by operators,</p>
                <p className="text-xs text-slate-300">not just traders.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-mist" data-testid="about-why">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-14">
          <Reveal>
            <p className="font-mono text-[11px] tracking-[0.3em] text-steel mb-4">WHY SAARTHVA EXISTS</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-navy leading-tight">
              New venture.<br />Experienced operating DNA.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                Saarthva is a new company built around a simple observation: growing businesses lose too much
                management time coordinating suppliers, facilities, vendors and everyday operational requirements.
              </p>
              <p>
                The company may be new. The operating experience behind it is not. Saarthva has been shaped by
                years of hands-on experience across multi-site operations, logistics, hospitality, workplace
                operations, procurement, vendor management and customer experience.
              </p>
              <p>
                We are building Saarthva to be the partner we always wished businesses had — responsive,
                accountable and comfortable getting into the details.
              </p>
              <p className="font-display text-xl font-bold text-navy pt-2">Built by operators, not just traders.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal className="mb-12">
            <p className="font-mono text-[11px] tracking-[0.3em] text-steel mb-4">WHAT WE STAND FOR</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-navy">The Saarthi code.</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal key={v.title} delay={i * 0.08}>
                  <div className="h-full bg-white rounded-2xl border border-line p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-200" data-testid={`value-card-${i}`}>
                    <span className="w-11 h-11 rounded-xl bg-cream grid place-items-center mb-5">
                      <Icon className="w-5 h-5 text-saffron" strokeWidth={1.8} />
                    </span>
                    <h3 className="font-display text-lg font-bold text-navy">{v.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-navy text-white grain relative overflow-hidden" data-testid="about-founder">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-4">
            <div className="flex items-center gap-5">
              <span className="w-20 h-20 rounded-full bg-saffron grid place-items-center font-display text-2xl font-extrabold text-white shrink-0">
                AV
              </span>
              <div>
                <p className="font-mono text-[11px] tracking-[0.3em] text-saffron mb-1.5">THE FOUNDER</p>
                <p className="font-display text-2xl font-bold">Abhimanyu Verma</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-8">
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
              Operator-led from day one.
            </h2>
            <div className="mt-5 space-y-4 text-slate-300 leading-relaxed max-w-2xl">
              <p>
                Abhimanyu brings 13+ years of experience across logistics, ecommerce, workplace operations,
                hospitality, procurement, customer experience and multi-site business operations in India and
                the United States.
              </p>
              <p>
                His experience running distributed teams, vendors, facilities and operating P&amp;Ls shaped
                Saarthva's core belief:
              </p>
              <p className="font-display text-xl font-bold text-white border-l-2 border-saffron pl-5">
                Sourcing something is only half the job. Making sure it works in the real world is what creates value.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
